import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
  children?: React.ReactNode;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  schemaJson,
  children,
}: SEOHeadProps) {
  useEffect(() => {
    const fullTitle = `${title} | 久久热`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);

    if (keywords) {
      setMeta('keywords', keywords);
    }

    setMeta('og:title', ogTitle || fullTitle, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:type', ogType, true);

    if (canonicalUrl) {
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.rel = 'canonical';
        document.head.appendChild(linkEl);
      }
      linkEl.href = canonicalUrl;

      const ogUrl = ogType !== 'website' ? canonicalUrl : 'https://mosquitos.cn';
      setMeta('og:url', ogUrl, true);
    }

    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', twitterCard);
    setMeta('twitter:title', ogTitle || fullTitle);
    setMeta('twitter:description', ogDescription || description);

    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    return () => {
      document.title = '久久热 - 热门内容持续更新，尽享精彩';
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogImage, twitterCard]);

  useEffect(() => {
    if (!schemaJson) return;

    const schemas = Array.isArray(schemaJson) ? schemaJson : [schemaJson];
    const scriptIds: string[] = [];

    schemas.forEach((schema, index) => {
      const scriptId = `schema-ld-${index}`;
      scriptIds.push(scriptId);

      let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = scriptId;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema);
    });

    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [schemaJson]);

  return <>{children}</>;
}